import { Box, Collapse, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { OwnerType, TOwnershipStructure } from '../../../types';
import Delete from '~/assets/icons/common/Delete.svg?react';
import { ExpandMore, Error } from '@mui/icons-material';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { useState } from 'react';
import { FormInput } from '~/components/FormComponents/FormInput';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { forbidenCitizenshipOprions, forbidenResidencyOprions } from '../../../constants';
import { theme } from '~/theme';
import { getOwnerTypeIcon } from '../../../utils/utils';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { commonStyles } from '~/theme/styles/commonStyles';
import { styles } from '../../../styles';

interface IForbidenCountryOwnerCardProps {
  isLegalEntity?: boolean;
  handleDelete: (index: number) => void;
  index: number;
  prefix: string;
  isError?: boolean;
}

export const ForbidenCountryOwnerCard = React.memo(
  ({
    isLegalEntity = false,
    handleDelete,
    index,
    prefix,
    isError,
  }: IForbidenCountryOwnerCardProps) => {
    const { trigger, control } = useFormContext<TOwnershipStructure>();
    const [expanded, setExpanded] = useState(true);

    const [fullName, entityName] = useWatch({
      control,
      name: [
        `${prefix}.${index}.fullName` as Path<TOwnershipStructure>,
        `${prefix}.${index}.entityName` as Path<TOwnershipStructure>,
      ],
    });

    const type = isLegalEntity ? OwnerType.LEGAL_ENTITY : OwnerType.INDIVIDUAL;
    const ownerNameField = isLegalEntity ? 'entityName' : 'fullName';
    const ownerNameFieldLabel = isLegalEntity
      ? 'Повне найменування'
      : "Прізвище, Ім'я, По батькові";
    const title = (fullName as string) || (entityName as string) || `${ownerNameFieldLabel}`;
    const titleColor = fullName || entityName ? 'initial' : 'grey';

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[100]}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box sx={styles.forbidenCountriesHeader}>
          <Box sx={commonStyles.centerFlexContainer}>
            <ExpandMore
              onClick={handleExpandClick}
              sx={{ transform: `rotate(${expanded ? 0 : -90}deg)`, cursor: 'pointer' }}
              color={isError ? 'error' : 'action'}
            />
            {getOwnerTypeIcon(type)}
            <Typography sx={{ color: titleColor }}>{title}</Typography>
            {isError && (
              <Tooltip title={'Перевірте коректність заповнення даних'} placement="top">
                <Error color="error" />
              </Tooltip>
            )}
          </Box>
          <IconButton sx={{ width: 35, height: 35 }} onClick={() => handleDelete(index)}>
            <Delete />
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <FormGridItem>
              <FormInput
                name={`${prefix}.${index}.${ownerNameField}`}
                label={ownerNameFieldLabel}
                placeholder={`Введіть ${ownerNameFieldLabel.toLowerCase()}`}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
            </FormGridItem>
            <FormGridItem>
              <FormInput
                name={`${prefix}.${index}.ownershipShare`}
                label="Частка володіння (%)"
                placeholder="Введіть частку володіння"
                fullWidth
              />
            </FormGridItem>
            <FormGridItem>
              <FormAutocomplete
                name={`${prefix}.${index}.residencyStatus`}
                label={`Статус резидентства ${isLegalEntity ? '' : '(за наявності)'}`}
                onChange={() => {
                  trigger(`${prefix}.${index}.citizenshipStatus` as Path<TOwnershipStructure>);
                }}
                onBlur={() => {
                  trigger(`${prefix}.${index}.citizenshipStatus` as Path<TOwnershipStructure>);
                }}
                options={forbidenResidencyOprions}
                inputProps={{
                  placeholder: 'Оберіть значення',
                }}
              />
            </FormGridItem>
            {!isLegalEntity && (
              <FormGridItem>
                <FormAutocomplete
                  name={`${prefix}.${index}.citizenshipStatus`}
                  label="Статус громадянства (за наявності)"
                  onChange={() => {
                    trigger(`${prefix}.${index}.residencyStatus` as Path<TOwnershipStructure>);
                  }}
                  onBlur={() => {
                    trigger(`${prefix}.${index}.residencyStatus` as Path<TOwnershipStructure>);
                  }}
                  options={forbidenCitizenshipOprions}
                  inputProps={{
                    placeholder: 'Оберіть значення',
                  }}
                />
              </FormGridItem>
            )}
          </Grid>
        </Collapse>
      </Box>
    );
  }
);
